// Encounter review — display-only note parsing. Never mutates the stored text.

const UNCLEAR = /not documented|not specified|not stated|unclear/gi;

// A heading is a short line ending in ":" OR a short all-caps line.
export function isHeading(line) {
  const t = String(line || "").trim();
  if (!t) return false;
  if (/^.{1,60}:\s*$/.test(t) && /[A-Za-z]/.test(t)) return true;
  if (/^[A-Z0-9 ,&/()\-]{2,40}$/.test(t) && (t.match(/[A-Z]/g) || []).length >= 2 && !/[a-z]/.test(t)) return true;
  return false;
}

// Split note text into [{ h, body }]. A leading block with no heading gets h:"".
export function parseSections(text) {
  const lines = String(text || "").split("\n");
  const secs = []; let cur = null;
  for (const raw of lines) {
    if (isHeading(raw)) { cur = { h: raw.trim().replace(/:\s*$/, ""), body: [] }; secs.push(cur); }
    else if (cur) cur.body.push(raw);
    else { cur = { h: "", body: [raw] }; secs.push(cur); }
  }
  return secs
    .map((s) => ({ h: s.h, body: s.body.join("\n").replace(/^\n+|\n+$/g, "") }))
    .filter((s) => s.h || s.body.trim());
}

export const isNotDocumented = (body) => String(body || "").trim().toLowerCase() === "not documented";

const HL = [/chief complaint/i, /assessment\s*(?:&|and)\s*plan/i, /prescriptions?/i, /plan\b/i];
export const isHighlight = (h) => HL.some((re) => re.test(String(h || "")));

// Count the "unclear / not stated" phrases across sections that are not themselves "Not documented".
export function countUnclear(sections) {
  let n = 0;
  for (const s of sections) {
    if (isNotDocumented(s.body)) continue;
    const m = s.body.match(UNCLEAR);
    if (m) n += m.length;
  }
  return n;
}

// Classify a single body line for display rendering.
export function classifyLine(line) {
  const t = String(line);
  const trimmed = t.trim();
  if (!trimmed) return { type: "blank" };
  let m;
  if ((m = trimmed.match(/^(\d+)[.)]\s+(.*)$/))) return { type: "num", num: m[1], text: m[2] };
  if ((m = trimmed.match(/^[-–]\s+(.*)$/))) {
    const body = m[1];
    const drug = body.match(/^([A-Za-z][A-Za-z0-9\-' ]+?)\s+(\d.*)$/);
    return drug ? { type: "bullet", drugName: drug[1], rest: drug[2] } : { type: "bullet", text: body };
  }
  if ((m = trimmed.match(/^([A-Za-z][A-Za-z /-]{2,24}):\s+(.+)$/))) return { type: "kv", label: m[1], value: m[2] };
  return { type: "p", text: trimmed };
}

// Split a string into alternating [{unclear:false,text}, {unclear:true,text}] for highlight rendering.
export function splitUnclear(text) {
  const out = []; let last = 0; const re = new RegExp(UNCLEAR.source, "gi"); let m;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ unclear: false, text: text.slice(last, m.index) });
    out.push({ unclear: true, text: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ unclear: false, text: text.slice(last) });
  return out;
}

// Plain-text copy of a section: heading + body exactly as stored.
export const sectionCopyText = (s) => (s.h ? `${s.h}:\n${s.body}` : s.body);
